import "../css/Header.css";
import { Link, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../services/authContext";
import { useFeatureConfig } from "../services/featureConfigContext";
import { useEffect, useState } from "react";
import NotificationBell from './Notifications/NotificationBell';
import InstallPrompt from './Pwa/InstallPrompt';
import api from "../services/api";

function Header() {
  const { user, logout } = useAuth();
  const { features } = useFeatureConfig();
  const { t } = useTranslation();
  const [subscription, setSubscription] = useState(null);
  const [accessPendingCount, setAccessPendingCount] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const loadSubscription = async () => {
      if (!user || user.role !== "user" || !features.payments) {
        setSubscription(null);
        return;
      }

      try {
        const data = await api.getMySubscription();
        if (!cancelled) {
          setSubscription(data);
        }
      } catch {
        if (!cancelled) {
          setSubscription(null);
        }
      }
    };

    loadSubscription();

    return () => {
      cancelled = true;
    };
  }, [user, features.payments]);

  useEffect(() => {
    let cancelled = false;

    const loadPendingCount = async () => {
      if (!user || user.role !== "user") {
        setAccessPendingCount(0);
        return;
      }

      try {
        const data = await api.getUnreadNotificationsCount();
        if (!cancelled) {
          setAccessPendingCount(data.count || 0);
        }
      } catch {
        if (!cancelled) {
          setAccessPendingCount(0);
        }
      }
    };

    loadPendingCount();
    const interval = setInterval(loadPendingCount, 60000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [user]);

  const subscriptionLabel = subscription?.active
    ? t('nav.subscriptionUntil', {
        date: new Date(subscription.expiresAt).toLocaleDateString('ru-RU'),
      })
    : t('nav.subscribe');

  return (
    <header className="app-header">
      <div className="app-shell header-row">
        <Link to="/" className="brand-link">
          <img src={`${process.env.PUBLIC_URL}/static/logo.svg`} className="brand-logo" alt="Логотип" />
          <div className="brand-text">
            <span className="brand-title">Psy-Trainer</span>
            <span className="brand-subtitle">Тесты и динамика состояния</span>
          </div>
        </Link>

        <nav className="top-nav" aria-label="Основная навигация">
          <NavLink to="/" end className={({ isActive }) => `top-nav__link ${isActive ? "is-active" : ""}`}>{t('nav.home')}</NavLink>
          <NavLink to="/tests" className={({ isActive }) => `top-nav__link ${isActive ? "is-active" : ""}`}>{t('nav.tests')}</NavLink>
          <NavLink to="/contacts" className={({ isActive }) => `top-nav__link ${isActive ? "is-active" : ""}`}>{t('nav.contacts')}</NavLink>
          {user && (
            <>
              <NavLink to="/results" className={({ isActive }) => `top-nav__link ${isActive ? "is-active" : ""}`}>{t('nav.results')}</NavLink>
              {user.role === "user" && features.payments && (
                <NavLink to="/subscription" className={({ isActive }) => `top-nav__link ${isActive ? "is-active" : ""}`}>
                  {subscriptionLabel}
                </NavLink>
              )}
              {user.role === "user" && features.messaging && (
                <NavLink to="/messages" className={({ isActive }) => `top-nav__link ${isActive ? "is-active" : ""}`}>
                  {t('nav.messages')}
                </NavLink>
              )}
              {user.role === "user" && features.psychologist && (
                <>
                  <NavLink to="/profile/access" className={({ isActive }) => `top-nav__link top-nav__link--with-badge ${isActive ? "is-active" : ""}`}>
                    {t('nav.access')}
                    {accessPendingCount > 0 && (
                      <span className="top-nav__badge">{accessPendingCount > 9 ? "9+" : accessPendingCount}</span>
                    )}
                  </NavLink>
                </>
              )}
              {user.role === "psychologist" && features.psychologist && (
                <NavLink to="/psychologist/users" className={({ isActive }) => `top-nav__link ${isActive ? "is-active" : ""}`}>{t('nav.users')}</NavLink>
              )}
              {user.role === "admin" && (
                <NavLink to="/admin" className={({ isActive }) => `top-nav__link ${isActive ? "is-active" : ""}`}>{t('nav.admin')}</NavLink>
              )}
            </>
          )}
        </nav>

        <div className="header-actions">
          {features.pwa && <InstallPrompt />}
          {user?.role === "user" && (features.psychologist || features.recommendations) && <NotificationBell />}
          {user ? (
            <>
              <Link to="/profile" className="ui-btn ui-btn-ghost profile-link">
                <img src="/user.webp" className="user-icon" alt="Профиль" />
                {user.username || t('auth.profile')}
              </Link>
              <button onClick={async () => await logout()} className="ui-btn ui-btn-primary" type="button">
                {t('auth.logout')}
              </button>
            </>
          ) : (
            <Link to="/login" className="ui-btn ui-btn-primary">
              {t('auth.login')}
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}

export default Header;
